/* eslint-disable react-hooks/exhaustive-deps */
import React, { forwardRef, useImperativeHandle, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// @mui
import { Card, Typography, Button, Dialog, DialogTitle, DialogActions } from '@mui/material';
// components
import { DentallyBookingFormPayment } from '../../../../components/dentally';
import { useSelector, dispatch } from '../../../../redux/store';
import { updateCompleteStatus, saveNewLeadDetail } from '../../../../redux/slices/lead';
import { getStripe } from '../../../../redux/slices/practiceIntegration';

// Map saved Redux payment state back to the DentallyBookingFormPayment values format
const mapFromRedux = (payment, type) => {
    if (!payment || payment.payment_type === 'No') {
        return {
            paymentOption: type === 'lead' ? 'received' : 'none',
            paymentType: 'DEPOSIT',
            paymentAmount: payment?.amount || '',
            receivedMode: 'PHONE',
            paymentLinkChannel: 'email',
        };
    }
    const isLink = payment.mode_of_payment === 'SEND';
    return {
        paymentOption: isLink ? 'link' : 'received',
        paymentType: payment.payment_type || 'DEPOSIT',
        paymentAmount: payment.amount || '',
        receivedMode: isLink ? 'PHONE' : (payment.mode_of_payment || 'PHONE'),
        paymentLinkChannel: payment.paymentLinkChannel || 'email',
    };
};

const PaymentOption = forwardRef(({ handleNext, type }, ref) => {
    const { newLeadDetail: { payment }, leadDetail } = useSelector((state) => state.lead);
    const { newLeadDetail } = useSelector((state) => state.lead);
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const { stripeConnected } = useSelector((state) => state.practiceIntegration);

    const [open, toggleOpen] = useState(false);
    const [values, setValues] = useState(() => mapFromRedux(payment, type));

    useEffect(() => {
        if (practiceId) {
            dispatch(getStripe(practiceId));
        }
    }, [practiceId]);

    const handleChange = (field, val) => {
        setValues(prev => ({ ...prev, [field]: val }));

        // Check Stripe connection when switching to payment link
        if (field === 'paymentOption' && val === 'link' && stripeConnected.length === 0) {
            toggleOpen(true);
        }
    };

    const onSubmit = (status) => {
        // Validate amount when payment is selected
        if (values.paymentOption !== 'none') {
            const amt = parseFloat(values.paymentAmount);
            if (!amt || amt <= 0) return;
        }

        // Map to the existing Redux save format
        const paymentData = {};
        if (values.paymentOption === 'none') {
            paymentData.payment_type = 'No';
            paymentData.mode_of_payment = '';
            paymentData.amount = 0;
        } else if (values.paymentOption === 'link') {
            paymentData.payment_type = values.paymentType;
            paymentData.mode_of_payment = 'SEND';
            paymentData.amount = parseFloat(values.paymentAmount) || 0;
            paymentData.paymentLinkChannel = values.paymentLinkChannel;
        } else {
            paymentData.payment_type = values.paymentType;
            paymentData.mode_of_payment = values.receivedMode;
            paymentData.amount = parseFloat(values.paymentAmount) || 0;
        }

        dispatch(saveNewLeadDetail({ payment: paymentData }));
        if (status?.toLowerCase?.() === 'finish') {
            dispatch(updateCompleteStatus(true));
            handleNext(true);
            return;
        }
        handleNext(true);
    };

    useImperativeHandle(
        ref, () => ({
            async submit(status) {
                onSubmit(status);
            },
        }),
        [values]
    );

    const location = window.location.origin;
    const handleClose = () => toggleOpen(false);
    const goToPracticeIntegrationPage = () => {
        window.open(`${location}/dashboard/user/integration`, '_blank');
        toggleOpen(false);
    };

    return (
        <>
            <Card sx={{ p: 3, boxShadow: 'none' }}>
                <Typography variant='subtitle2' sx={{ mb: 1, fontSize: 13, fontWeight: 600 }}>
                    Take a deposit or consultation fee for {newLeadDetail?.lead_treatment?.treatment || leadDetail?.lead_treatments?.[0]?.treatment || ''}?
                </Typography>
                <DentallyBookingFormPayment
                    values={values}
                    onChange={handleChange}
                    errors={{}}
                />
            </Card>
            <Dialog open={open} onClose={handleClose}>
                <DialogTitle sx={{ padding: '40px 40px 0px', textAlign: 'center' }}>Stripe connection is required to send an online payment link. Do you want to redirect to the Practice Integration page to connect your Stripe account?</DialogTitle>
                <DialogActions sx={{ padding: '40px !important', justifyContent: 'center' }}>
                    <Button sx={{ margin: '0px 10px', padding: '5px 20px', fontSize: '14px', boxShadow: 'none', border: '1px solid rgba(145, 158, 171, 0.32)', borderRadius: '8px', minWidth: '100px' }} onClick={handleClose}>No</Button>
                    <Button type="submit" sx={{ margin: '0px 10px', padding: '5px 20px', fontSize: '14px', boxShadow: '0px 8px 16px rgba(0, 171, 85, 0.24)', borderRadius: '8px', bgcolor: '#00AB55 !important', color: '#fff', minWidth: '100px' }} onClick={goToPracticeIntegrationPage}>Yes</Button>
                </DialogActions>
            </Dialog>
        </>
    );
});

PaymentOption.propTypes = {
    handleNext: PropTypes.func,
    type: PropTypes.string,
};
export default PaymentOption;
